import { createEmptyDocument } from "../scene/factory";
import { createDeviceElement } from "../scene/ops";
import { getPreset } from "../scene/presets";
import type { Paint, SceneDocument, Shadow } from "../scene/schema";
import { deviceBoxSize, deviceContentAspect, type PixelSize } from "./element";
import { type DeviceProps, deviceExtent } from "./layout";

/**
 * A new design made from one screenshot ("Mockup erstellen" in the media
 * library, docs/19-mockups.md): the device centred on a background, either
 * in a social media format or in a page that fits the device.
 */

export const MOCKUP_BACKGROUNDS = [
  { id: "transparent", label: "Transparent", paint: "#ffffff00" },
  { id: "white", label: "Weiß", paint: "#ffffff" },
  { id: "light", label: "Hellgrau", paint: "#f1f2f4" },
  {
    id: "soft",
    label: "Verlauf",
    paint: {
      type: "linear",
      angle: 135,
      stops: [
        { offset: 0, color: "#e0e7ff" },
        { offset: 1, color: "#fce7f3" },
      ],
    },
  },
  {
    id: "night",
    label: "Nacht",
    paint: {
      type: "linear",
      angle: 160,
      stops: [
        { offset: 0, color: "#1e1b4b" },
        { offset: 1, color: "#0f172a" },
      ],
    },
  },
] as const satisfies readonly { id: string; label: string; paint: Paint }[];

export type MockupBackground = (typeof MOCKUP_BACKGROUNDS)[number]["id"];

/** Page formats: "fit" wraps the device with a margin; the others are social media presets. */
export const MOCKUP_FORMATS = [
  { id: "fit", label: "Passend zum Gerät" },
  { id: "square", label: "Quadrat 1:1" },
  { id: "instagram-portrait", label: "Hochformat 4:5" },
  { id: "story", label: "Story 9:16" },
  { id: "presentation", label: "Querformat 16:9" },
] as const;

export type MockupFormat = (typeof MOCKUP_FORMATS)[number]["id"];

/** Longer side of a "fit" page in pixels (sharp enough for print and retina screens). */
const FIT_SIDE = 2000;

export function createMockupDocument(input: {
  device: DeviceProps;
  screenshot: { id: string } & PixelSize;
  format: MockupFormat;
  background: MockupBackground;
  /**
   * Only the device (mockup image for the media library): the page is the
   * device's box, transparent and without a shadow. Ignores `format` and
   * `background`.
   */
  bare?: boolean;
  /** Longer side of a "fit" page (default 2000 px). */
  side?: number;
}): SceneDocument {
  const contentAspect = deviceContentAspect(input.screenshot);
  const background = input.bare ? "transparent" : input.background;
  const format = input.bare ? "fit" : input.format;
  const paint = MOCKUP_BACKGROUNDS.find((entry) => entry.id === background)?.paint ?? "#ffffff";
  let width: number;
  let height: number;
  let preset = "custom";
  let share = 0.78;
  if (format === "fit") {
    const extent = deviceExtent(input.device, contentAspect);
    const aspect = extent.width / extent.height;
    share = input.bare ? 1 : 0.88;
    const long = input.side ?? FIT_SIDE;
    width = Math.round(aspect >= 1 ? long : long * aspect);
    height = Math.round(aspect >= 1 ? long / aspect : long);
  } else {
    const found = getPreset(format);
    width = found?.width ?? 1080;
    height = found?.height ?? 1080;
    preset = found?.id ?? "square";
  }
  const doc = createEmptyDocument(
    { id: preset, label: "Mockup", category: "social", kind: "design", width, height },
    "#ffffff",
  );
  // Gradients are valid page backgrounds (paintSchema).
  (doc.pages[0] as { background: Paint }).background = JSON.parse(JSON.stringify(paint));
  const size = deviceBoxSize(input.device, contentAspect, {
    width: width * share,
    height: height * share,
  });
  const side = Math.max(size.width, size.height);
  const shadow: Shadow = {
    x: 0,
    y: Math.round(side * 0.03),
    blur: Math.round(side * 0.08),
    color: background === "night" ? "#00000080" : "#0000003d",
  };
  const element = createDeviceElement({
    ...input.device,
    tilt: input.device.tilt ?? { x: 0, y: 0, z: 0 },
    x: Math.round((width - size.width) / 2),
    y: Math.round((height - size.height) / 2),
    ...size,
    ...(input.bare ? {} : { shadow }),
    assetId: input.screenshot.id,
  });
  doc.elements[element.id] = element;
  doc.pages[0]?.children.push(element.id);
  return doc;
}
